import { supabase, safeGetSession } from './supabaseClient.js';
import { AI_ABORT_MS } from './aiTimeouts.js';

/**
 * Conversation Memory System
 *
 * Extracts and persists concrete facts from DM conversations so characters
 * remember details across messages and across separate conversations.
 *
 * Flow:
 * 1. After each AI reply, extractFacts() runs in the background
 * 2. It sends the recent messages to a lightweight AI call that pulls out facts
 * 3. Facts are stored in dm_conversation_facts (per conversation)
 * 4. getCharacterMemory() aggregates facts across ALL conversations with a character
 * 5. formatMemoryForPrompt() builds the "THINGS YOU KNOW" system prompt section
 *
 * Fact categories:
 * - name: Player's name, character name, nicknames
 * - relationship: Who the player is to the character, relationship status
 * - preference: Things the player likes/dislikes, habits
 * - event: Things that happened in the story (key plot points)
 * - emotion: Emotional revelations, fears, hopes, vulnerabilities
 * - promise: Things either party committed to doing
 * - detail: Misc concrete details (job, hometown, pet's name, etc.)
 */

// ── Debounce: only extract once per conversation per N new messages ────────
const EXTRACT_EVERY_N_MESSAGES = 2; // Run extraction every 2 new AI replies
const MAX_FACTS_PER_CONVERSATION = 30; // Cap to keep prompt size reasonable
const CONTEXT_BEFORE_NEW = 4;          // older rows re-read for context ahead of the new ones
const MAX_ROWS_PER_EXTRACTION = 60;    // a cursor far behind catches up on the newest window
const MAX_FACTS_IN_PROMPT = 40; // Max facts to inject across all conversations

// Track in-flight extractions to prevent duplicates
const _extractionInFlight = {};

/**
 * Extract facts from a conversation's recent messages.
 * Called as a fire-and-forget background task after each AI reply.
 *
 * @param {string} conversationId - The DM conversation ID
 * @param {string} characterId - The character's ID
 * @param {string} characterName - The character's display name
 * @param {string} playerLabel - The player's display name
 * @param {Array} recentMessages - The messages array (already in memory)
 * @param {string} userId - The authenticated user's ID
 * @param {string} aiProvider - 'gemini' or 'claude'
 */
export async function extractFacts(
  conversationId, characterId, characterName, playerLabel,
  recentMessages, userId, _aiProvider, opts = {}
) {
  if (!conversationId || !characterId || !userId) return;
  // dm_conversation_facts.conversation_id is a foreign key to dm_conversations.
  // The story path used to call this with a STORY id: every insert violated the
  // key and the error was never read (2026-10-19 audit: 0 story rows ever
  // landed). Story knowledge has its own ledgers (story_events, character_facts).
  if ((opts.threadType || 'dm') !== 'dm') return;
  if (_extractionInFlight[conversationId]) return;

  try {
    _extractionInFlight[conversationId] = true;

    // Check existing facts to see how many messages we've already processed
    const { data: existing } = await supabase
      .from('dm_conversation_facts')
      .select('id, facts, messages_processed')
      .eq('conversation_id', conversationId)
      .maybeSingle();

    const messagesProcessed = existing?.messages_processed || 0;
    // THE CURSOR IS THE DATABASE'S COUNT, NEVER THE LOADED PAGE (2026-10-19
    // audit). `recentMessages` is the UI page (DM_PAGE_SIZE 100, at most 400
    // rows) and this compared its LENGTH against a stored cursor that counts the
    // whole thread: once the cursor passed the page the difference went negative
    // forever and THINGS YOU KNOW stopped learning — Nicole's main Ford thread
    // sat at 1,014 of 9,689 rows with its last fact from 2026-08-02. The same
    // defect froze the rolling summary once (rollingSummary.js getTrueMessageCount).
    const { count: trueCount, error: countErr } = await supabase
      .from('dm_messages')
      .select('id', { count: 'exact', head: true })
      .eq('conversation_id', conversationId);
    if (countErr) { console.warn('[Memory] fact extraction: count failed —', countErr.message); return; }
    const totalMessages = typeof trueCount === 'number' ? trueCount : recentMessages.length;

    // Only extract if enough new messages have accumulated
    if (totalMessages - messagesProcessed < EXTRACT_EVERY_N_MESSAGES) return;

    // Get session token for the AI call
    const { data: { session } } = await safeGetSession();
    if (!session?.access_token) return;

    // The unprocessed rows come from the database too — the page may not hold
    // them. A cursor far behind (the frozen threads above) catches up on the
    // newest window instead of replaying thousands of rows; the nightly
    // summaries already hold the middle. Canned starter lines are not memory
    // (Nicole, 2026-10-06; v406 retired them) and are dropped here as well.
    const want = Math.min(Math.max(totalMessages - messagesProcessed, 0) + CONTEXT_BEFORE_NEW, MAX_ROWS_PER_EXTRACTION);
    const { data: freshRows, error: rowsErr } = await supabase
      .from('dm_messages')
      .select('sender_type, content, is_ooc, created_at, metadata')
      .eq('conversation_id', conversationId)
      .order('created_at', { ascending: false })
      .limit(want);
    if (rowsErr) { console.warn('[Memory] fact extraction: rows failed —', rowsErr.message); return; }
    const relevantMsgs = (freshRows || []).slice().reverse()
      .filter(m => !(m.metadata && typeof m.metadata === 'object' && 'daily_starter' in m.metadata));

    const messagesText = relevantMsgs
      .filter(m => m.content && m.content.trim() && !m.is_ooc)
      .map(m => {
        const speaker = m.sender_type === 'user' ? playerLabel : characterName;
        return `${speaker}: ${String(m.content).substring(0, 400)}`;
      })
      .join('\n');

    if (!messagesText || messagesText.length < 50) return;

    // Include existing facts so the AI can deduplicate and update
    const existingFacts = existing?.facts || [];
    const existingFactsText = existingFacts.length > 0
      ? `\nALREADY KNOWN FACTS (update or keep, don't duplicate):\n${existingFacts.map(f => `- [${f.category}] ${f.fact}`).join('\n')}`
      : '';

    const systemPrompt = `You extract concrete, memorable facts from a roleplay DM conversation between ${characterName} and ${playerLabel}.

Extract ONLY specific, factual details that ${characterName} would remember about ${playerLabel} and their shared experiences. Focus on:
- Names, nicknames, titles the player uses or is called
- Relationships (who the player is to others, romantic status, family)
- Preferences (favorite things, dislikes, habits, hobbies)
- Key events (what happened between them, important scenes)
- Emotional revelations (fears, dreams, vulnerabilities shared)
- Promises or commitments made by either party
- Concrete details (job, species, powers, hometown, pet names, etc.)

DO NOT extract:
- Vague observations ("they seem nice")
- Things ${characterName} said about themselves (only extract facts ABOUT the player or the relationship)
- Duplicate facts already in the known list
${existingFactsText}

Respond ONLY with a JSON array. Each item: {"category":"name|relationship|preference|event|emotion|promise|detail","fact":"concise fact text","importance":1-3}
importance: 3=critical (name, relationship status), 2=significant (key events, strong preferences), 1=minor detail
CORRECTIONS: if ${playerLabel} corrects or contradicts a known fact (e.g. "no, that was ten years ago"), ${playerLabel} is right. Return {"category":"correction","fact":"<the corrected fact>","replaces":"<the known fact's text, or a distinctive part of it>","importance":2} and never keep the wrong version.
DATES: a fact that is about WHEN something happened names the date or how long ago it was as of ${new Date().toISOString().slice(0, 10)}.

If no new facts are found, respond with []`;

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const res = await fetch(`${supabaseUrl}/functions/v1/ai-proxy`, {
      method: 'POST',
      signal: AbortSignal.timeout(AI_ABORT_MS), // AI_ABORT_MS guard (audit P2-14; see aiTimeouts.js)
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        // Kept on Claude (with nsfw:true) because messagesText contains recent
        // DM turns that may include NSFW content. Gemini's HARASSMENT /
        // DANGEROUS_CONTENT filters can mangle extraction on darker scenes.
        provider: 'claude-haiku',
        nsfw: true,
        systemPrompt,
        messages: [{ role: 'user', content: messagesText }],
        // Headroom for reasoning models — see the note at the twin call site
        // in characterFacts.js. 500 was exhausted by hidden reasoning tokens
        // before any text was emitted.
        maxTokens: 1200,
        feature: 'fact_extraction',
        characterName,
      }),
    });

    if (!res.ok) {
      console.error('[Memory] Fact extraction API error:', res.status);
      return;
    }

    const data = await res.json();
    const responseText = data.response || data.content || data.text || '';

    // Parse the JSON array from the response
    let newFacts = [];
    try {
      // Extract JSON from response (handle markdown code blocks)
      const jsonMatch = responseText.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        newFacts = JSON.parse(jsonMatch[0]);
      }
    } catch (parseErr) {
      console.warn('[Memory] Failed to parse facts JSON:', parseErr.message);
      return;
    }

    if (!Array.isArray(newFacts) || newFacts.length === 0) return;

    // Corrections first: the player contradicted a known fact, so the wrong
    // version goes (2026-10-06, Nicole: a reaction from ten years ago lived on
    // as "three weeks of itching").
    const corrections = newFacts.filter(f => f && f.category === 'correction' && typeof f.fact === 'string' && f.fact.trim());
    let existingAfterCorrections = existingFacts;
    for (const c of corrections) {
      const phrase = String(c.replaces || '').toLowerCase().replace(/\s+/g, ' ').trim();
      if (phrase.length >= 12) {
        existingAfterCorrections = existingAfterCorrections.filter(e => !String(e.fact || '').toLowerCase().replace(/\s+/g, ' ').includes(phrase.slice(0, 60)));
      }
    }
    newFacts = newFacts.map(f => (f && f.category === 'correction') ? { ...f, category: 'detail' } : f);
    // Validate and clean facts
    const validCategories = ['name', 'relationship', 'preference', 'event', 'emotion', 'promise', 'detail'];
    newFacts = newFacts
      .filter(f => f && typeof f.fact === 'string' && f.fact.trim())
      .filter(f => validCategories.includes(f.category))
      .map(f => ({
        category: f.category,
        fact: f.fact.trim().substring(0, 200), // Cap individual fact length
        importance: Math.min(3, Math.max(1, parseInt(f.importance) || 1)),
        // 2026-10-06 (Nicole: "pulling from old scenes"): a fact without a date
        // reads as the present forever. Stamped on the day it was learned.
        learned_at: new Date().toISOString().slice(0, 10),
      }));

    // Merge with existing facts: deduplicate by checking similarity
    const mergedFacts = [...existingAfterCorrections];
    for (const newFact of newFacts) {
      const isDuplicate = mergedFacts.some(existing =>
        existing.category === newFact.category &&
        existing.fact.toLowerCase().includes(newFact.fact.toLowerCase().substring(0, 30))
      );
      if (!isDuplicate) {
        mergedFacts.push(newFact);
      }
    }

    // Cap total facts — keep highest importance first, then most recent
    const cappedFacts = mergedFacts
      .sort((a, b) => (b.importance || 1) - (a.importance || 1))
      .slice(0, MAX_FACTS_PER_CONVERSATION);

    // Upsert to database
    const row = {
      conversation_id: conversationId,
      character_id: characterId,
      user_id: userId,
      facts: cappedFacts,
      messages_processed: totalMessages,
      updated_at: new Date().toISOString(),
    };

    const { error: writeErr } = existing
      ? await supabase.from('dm_conversation_facts').update(row).eq('id', existing.id)
      : await supabase.from('dm_conversation_facts').insert(row);
    if (writeErr) { console.warn('[Memory] fact write failed:', writeErr.message); return; }

    console.log(`[Memory] Extracted ${newFacts.length} new facts for ${characterName} (total: ${cappedFacts.length}; cursor ${messagesProcessed} → ${totalMessages})`);
  } catch (err) {
    console.error('[Memory] Fact extraction error:', err);
  } finally {
    delete _extractionInFlight[conversationId];
  }
}


/**
 * Get aggregated facts for a character across ALL conversations with a user.
 * This gives the character cross-conversation memory.
 *
 * @param {string} characterId - The character's ID
 * @param {string} userId - The user's ID
 * @returns {Array} Deduplicated, importance-sorted facts
 */
export async function getCharacterMemory(characterId, userId) {
  try {
    const { data, error } = await supabase
      .from('dm_conversation_facts')
      .select('facts, created_at, updated_at')
      .eq('character_id', characterId)
      .eq('user_id', userId);

    if (error || !data || data.length === 0) return [];

    // Aggregate all facts across conversations. A fact stored before dates
    // were kept (2026-10-06) is dated by its row: learned no later than the
    // row's last update, which is true and keeps it in the past where it belongs.
    const allFacts = [];
    for (const row of data) {
      const rowDate = String(row.updated_at || row.created_at || '').slice(0, 10) || null;
      if (Array.isArray(row.facts)) {
        allFacts.push(...row.facts.map(f => (f && !f.learned_at && rowDate) ? { ...f, learned_by: rowDate } : f));
      }
    }

    // Deduplicate: merge similar facts (keep highest importance version)
    const unique = [];
    for (const fact of allFacts) {
      const existing = unique.find(u =>
        u.category === fact.category &&
        (u.fact.toLowerCase().includes(fact.fact.toLowerCase().substring(0, 30)) ||
         fact.fact.toLowerCase().includes(u.fact.toLowerCase().substring(0, 30)))
      );
      if (existing) {
        // Keep the more important / longer version
        if ((fact.importance || 1) > (existing.importance || 1) || fact.fact.length > existing.fact.length) {
          Object.assign(existing, fact);
        }
      } else {
        unique.push({ ...fact });
      }
    }

    // Sort: importance desc, then by category priority
    const categoryOrder = { name: 0, relationship: 1, detail: 2, preference: 3, event: 4, emotion: 5, promise: 6 };
    return unique
      .sort((a, b) => {
        const impDiff = (b.importance || 1) - (a.importance || 1);
        if (impDiff !== 0) return impDiff;
        return (categoryOrder[a.category] || 9) - (categoryOrder[b.category] || 9);
      })
      .slice(0, MAX_FACTS_IN_PROMPT);
  } catch (err) {
    console.error('[Memory] Error fetching character memory:', err);
    return [];
  }
}


/**
 * Format character memory facts into a system prompt section.
 *
 * @param {Array} facts - Array of fact objects from getCharacterMemory
 * @param {string} characterName - The character's name
 * @param {string} playerLabel - The player's display name
 * @returns {string} Formatted prompt section
 */
export function formatMemoryForPrompt(facts, characterName, playerLabel) {
  if (!facts || facts.length === 0) return '';

  // Group by category for clean formatting
  const groups = {};
  for (const fact of facts) {
    const cat = fact.category || 'detail';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(fact);
  }
  // Every line carries when it was learned. Nicole, 2026-10-06: "the DM memory
  // is pulling from old scenes" — Ford re-lived a June sushi night as if it were
  // tonight because "Shared History" had no dates on it.
  const dated = (f) => {
    if (f.learned_at) return ` (learned ${f.learned_at})`;
    if (f.learned_by) return ` (learned by ${f.learned_by})`;
    return ' (learned earlier; the past)';
  };

  const categoryLabels = {
    name: 'Identity',
    relationship: 'Your Relationship',
    preference: 'Their Preferences & Habits',
    event: 'Shared History',
    emotion: 'Things They\'ve Shared With You',
    promise: 'Promises & Commitments',
    detail: 'Things You Know',
  };

  let section = `\nTHINGS YOU (${characterName}) KNOW ABOUT ${playerLabel.toUpperCase()}:\n`;
  section += `(These are facts you've learned across your conversations. Reference them naturally — don't list them, but let them inform how you talk to this person. If they told you their dog's name, ask about the dog. If they shared a fear, be gentle around it. Each one carries the date it was learned: a memory is the PAST. A night from weeks or months ago is remembered, not happening now; never open a reply inside it or greet the player as if they are still in it. What is happening now is the conversation itself.)\n`;

  for (const [cat, items] of Object.entries(groups)) {
    const label = categoryLabels[cat] || cat;
    section += `\n${label}:\n`;
    for (const item of items) {
      section += `• ${item.fact}${dated(item)}\n`;
    }
  }

  return section;
}
